import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Share2, Copy, X, Instagram, Loader2, QrCode } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { profileUrl } from "../lib/config";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Reusable "Share to Instagram Story" sheet. Shows an animated, vibrant preview
// (the real server-rendered 9:16 PNG with motion overlays) plus download/share.
// When `qr` is true it becomes the profile's single unified share tool, with a
// Card / QR toggle so the scannable follow-link lives in the same place.
export function StoryShareModal({ username, options, onClose, imageUrl, link, filename, qr = false, cacheKey }) {
  const opts = options && options.length ? options : [{ type: "profile", label: "My Profile" }];
  const [active, setActive] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("card"); // card | qr
  const sel = opts[active];
  const single = !!imageUrl;

  const storyUrl = useMemo(() => {
    if (imageUrl) return imageUrl;
    const p = new URLSearchParams({ type: sel.type || "profile" });
    if (sel.title) p.set("title", sel.title);
    if (sel.subtitle) p.set("subtitle", sel.subtitle);
    if (sel.cover) p.set("cover", sel.cover);
    if (sel.tier) p.set("tier", sel.tier);
    if (cacheKey != null) p.set("v", String(cacheKey));
    return `${BACKEND_URL}/api/og/story/${encodeURIComponent(username)}.png?${p.toString()}`;
  }, [sel, username, imageUrl, cacheKey]);

  const profileLink = link || profileUrl(username);
  const dlName = filename || username;

  useEffect(() => { setLoaded(false); }, [storyUrl]);

  const fetchBlob = async () => {
    const r = await fetch(storyUrl);
    if (!r.ok) throw new Error("render failed");
    return await r.blob();
  };

  const download = async () => {
    setBusy(true);
    try {
      const blob = await fetchBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `virtustars-story-${dlName}.png`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Story card saved — add it to your Instagram story!");
    } catch { toast.error("Could not generate card"); } finally { setBusy(false); }
  };

  const share = async () => {
    setBusy(true);
    try {
      const blob = await fetchBlob();
      const file = new File([blob], `virtustars-story-${username}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "VirtuStars", text: `Follow me on VirtuStars — ${profileLink}` });
      } else if (navigator.share) {
        await navigator.share({ title: "VirtuStars", text: `Follow me on VirtuStars`, url: profileLink });
      } else {
        await navigator.clipboard.writeText(profileLink);
        toast.success("Profile link copied");
      }
    } catch (e) { if (e?.name !== "AbortError") toast.error("Share not available — try Save"); }
    finally { setBusy(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(profileLink); toast.success("Profile link copied"); }
    catch { toast.error("Could not copy"); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="story-share-modal" onClick={onClose}>
      <div className="w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-white font-semibold"><Instagram size={18} className="text-eblue" /> Share</div>
          <button onClick={onClose} data-testid="story-close-btn" className="text-white/70 hover:text-white"><X size={20} /></button>
        </div>

        {qr && (
          <div className="flex gap-1 mb-3 surface-2 rounded-full p-1" data-testid="share-view-toggle">
            {[["card", "Story card"], ["qr", "QR & link"]].map(([k, l]) => (
              <button key={k} onClick={() => setView(k)} data-testid={`share-view-${k}`}
                className={`flex-1 h-9 rounded-full text-sm font-semibold transition-colors flex items-center justify-center gap-1.5 ${view === k ? "bg-eblue text-white" : "text-muted-foreground hover:text-white"}`}>
                {k === "qr" ? <QrCode size={14} /> : <Instagram size={14} />}{l}
              </button>
            ))}
          </div>
        )}

        {qr && view === "qr" ? (
          <div data-testid="share-qr-panel">
            <div className="rounded-[22px] overflow-hidden bg-gradient-to-b from-indigo-950 via-blue-950 to-violet-950 p-6 flex flex-col items-center gap-4" style={{ aspectRatio: "9 / 16", maxHeight: "62vh" }}>
              <div className="flex-1 flex items-center justify-center">
                <div className="bg-white rounded-2xl p-4"><QRCodeCanvas value={profileLink} size={180} level="M" fgColor="#030303" bgColor="#ffffff" /></div>
              </div>
              <div className="text-center">
                <div className="font-display font-bold text-gold text-lg">@{username}</div>
                <div className="text-xs text-white/60 mt-0.5">Scan to follow on VirtuStars</div>
              </div>
            </div>
            <div className="flex items-center gap-2 surface-2 rounded-lg px-3 h-10 mt-3">
              <span className="text-xs font-mono truncate flex-1" data-testid="share-link-text">{profileLink}</span>
              <button onClick={copyLink} className="text-eblue shrink-0" data-testid="share-link-copy"><Copy size={15} /></button>
            </div>
          </div>
        ) : (
        <>
        {!single && opts.length > 1 && (
          <div className="flex gap-2 mb-3 overflow-x-auto no-scrollbar">
            {opts.map((o, i) => (
              <button key={o.type + i} onClick={() => setActive(i)} data-testid={`story-type-${o.type}`}
                className={`shrink-0 px-3.5 h-9 rounded-full text-sm font-medium transition-colors ${i === active ? "bg-eblue text-white" : "surface-2 text-muted-foreground hover:text-white"}`}>
                {o.label || o.type}
              </button>
            ))}
          </div>
        )}

        {/* Animated vibrant preview: real PNG + motion overlays */}
        <div className="relative mx-auto rounded-[22px] overflow-hidden shadow-2xl" style={{ aspectRatio: "9 / 16", maxHeight: "62vh" }}>
          <motion.div aria-hidden className="absolute -inset-1 rounded-[26px] z-0"
            animate={{ boxShadow: ["0 0 30px 4px rgba(74,142,255,.45)", "0 0 44px 8px rgba(242,199,74,.5)", "0 0 30px 4px rgba(139,74,245,.45)"] }}
            transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }} />
          {!loaded && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-gradient-to-b from-indigo-950 via-blue-900 to-violet-900">
              <Loader2 className="animate-spin text-white/70" />
            </div>
          )}
          <img src={storyUrl} alt="Story preview" data-testid="story-preview-img"
            onLoad={() => setLoaded(true)} className="relative z-10 w-full h-full object-cover" />
          {/* shimmer sweep */}
          <motion.div aria-hidden className="pointer-events-none absolute inset-0 z-20"
            initial={{ x: "-120%" }} animate={{ x: "120%" }}
            transition={{ duration: 2.6, repeat: Infinity, repeatDelay: 1.4, ease: "easeInOut" }}
            style={{ background: "linear-gradient(105deg, transparent 35%, rgba(255,255,255,.18) 50%, transparent 65%)" }} />
          {/* floating sparkles */}
          {[...Array(6)].map((_, i) => (
            <motion.span key={i} aria-hidden className="pointer-events-none absolute z-20 rounded-full"
              style={{ left: `${12 + i * 14}%`, top: `${20 + (i % 3) * 25}%`, width: 6, height: 6, background: i % 2 ? "#F2C74A" : "#4A8EFF" }}
              animate={{ y: [0, -14, 0], opacity: [0.2, 0.9, 0.2], scale: [0.8, 1.3, 0.8] }}
              transition={{ duration: 2.2 + i * 0.3, repeat: Infinity, ease: "easeInOut", delay: i * 0.25 }} />
          ))}
        </div>
        </>
        )}

        <div className="grid grid-cols-3 gap-2 mt-4">
          <Button onClick={download} disabled={busy} data-testid="story-download-btn" className="rounded-xl bg-eblue hover:bg-eblue/90 text-white">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <><Download size={15} className="mr-1" />Save</>}
          </Button>
          <Button onClick={share} disabled={busy} variant="outline" data-testid="story-share-btn" className="rounded-xl border-white/15">
            <Share2 size={15} className="mr-1" />Share
          </Button>
          <Button onClick={copyLink} variant="outline" data-testid="story-copy-btn" className="rounded-xl border-white/15">
            <Copy size={15} className="mr-1" />Link
          </Button>
        </div>
        <p className="text-center text-xs text-muted-foreground mt-3">Save the card, then open Instagram → add it to your Story.</p>
      </div>
    </div>
  );
}
